/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { TaxonomyCodeDto } from '@shared/types';
import { useAssignmentStore } from '../stores/assignmentStore';
import { TaxonomySearch } from '../components/assignment/TaxonomySearch';
import { TaxonomyBrowser } from '../components/assignment/TaxonomyBrowser';
import { CurrentAssignments } from '../components/assignment/CurrentAssignments';
import { PageHelp } from '../components/layout/PageHelp';
import { Tooltip } from '../components/layout/Tooltip';

export function AssignmentPage() {
  const { setId } = useParams<{ setId: string }>();
  const navigate = useNavigate();
  const {
    formula, isLoading, error, isSaving, saveError, successMessage,
    loadFormula, assign, batchAssignTaxonomies, reassign, remove, advanceToNext, clearSuccess,
  } = useAssignmentStore();

  const [reassignTarget, setReassignTarget] = useState<string | null>(null);

  useEffect(() => {
    if (setId) loadFormula(setId);
  }, [setId, loadFormula]);

  // Auto-clear success after 3s
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(clearSuccess, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, clearSuccess]);

  const handleSelect = useCallback(async (taxonomy: TaxonomyCodeDto) => {
    if (!formula) return;
    if (reassignTarget) {
      await reassign(reassignTarget, taxonomy.setId);
      setReassignTarget(null);
    } else {
      await assign(formula.setId, taxonomy.setId);
    }
  }, [formula, reassignTarget, assign, reassign]);

  const handleBatchSelect = useCallback(async (taxonomies: TaxonomyCodeDto[]) => {
    if (!formula || taxonomies.length === 0) return;
    await batchAssignTaxonomies(formula.setId, taxonomies.map((t) => t.setId));
  }, [formula, batchAssignTaxonomies]);

  const handleRemove = useCallback(async (xlinkSetId: string) => {
    if (confirm('Remove this taxonomy assignment?')) {
      await remove(xlinkSetId, 'Manually removed by pharmacist');
    }
  }, [remove]);

  const handleAdvance = useCallback(async () => {
    if (!formula) return;
    const nextSetId = await advanceToNext(formula.name);
    if (nextSetId) {
      navigate(`/formulas/${nextSetId}`);
    } else {
      navigate('/formulas');
    }
  }, [formula, advanceToNext, navigate]);

  // Keyboard shortcut: Ctrl+Enter = advance
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.ctrlKey && e.key === 'Enter') {
        handleAdvance();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleAdvance]);

  if (isLoading) {
    return <div className="text-center text-gray-400 py-12">Loading formula...</div>;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
        {error}
      </div>
    );
  }

  if (!formula) return null;

  return (
    <div className="max-w-3xl">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <button onClick={() => navigate('/formulas')} className="text-sm text-dds-blue-500 hover:underline mb-1">
            ← Back to Formulas
          </button>
          <h2 className="text-xl font-bold text-gray-900">{formula.name}</h2>
          {formula.genericName && <p className="text-gray-500 text-sm">{formula.genericName}</p>}
        </div>
        <Tooltip text="Jump to the next unassigned formula (Ctrl+Enter)">
          <button
            onClick={handleAdvance}
            className="bg-dds-teal-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-dds-teal-600"
            title="Ctrl+Enter"
          >
            Next Unassigned →
          </button>
        </Tooltip>
      </div>

      <PageHelp
        title="How to assign taxonomy codes"
        steps={[
          'Review the formula properties and ingredients above.',
          'Check the Suggested tab in the taxonomy browser — codes assigned to formulas with shared ingredients appear here.',
          'Use the search box for quick lookup by code or name, or browse with filters.',
          'Click a taxonomy code to assign it to this formula.',
          'Press Ctrl+Enter or click "Next Unassigned" to move to the next formula.',
        ]}
        tips={[
          'The "Suggested" tab ranks codes by shared ingredient count — higher is better.',
          'Use "Reassign" on an existing assignment to swap it for a different code.',
          'Ingredients shown as purple pills help identify the compound type.',
        ]}
        shortcuts={[
          { key: 'Ctrl+Enter', action: 'Next unassigned formula' },
        ]}
      />

      {/* Formula properties */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 grid grid-cols-2 gap-3 text-sm">
        <div><Tooltip text="Physical form of the medication (cream, capsule, etc.)"><span className="text-gray-500 cursor-help">Dosage Form:</span></Tooltip> <span className="font-medium">{formula.dosageForm || '—'}</span></div>
        <div><Tooltip text="Medication concentration or amount"><span className="text-gray-500 cursor-help">Strength:</span></Tooltip> <span className="font-medium">{formula.strength || '—'}</span></div>
        <div><Tooltip text="DEA controlled substance schedule (2–5)"><span className="text-gray-500 cursor-help">Schedule:</span></Tooltip> <span className="font-medium">{formula.schedule || '—'}</span></div>
        <div><Tooltip text="Administration route (topical, oral, etc.)"><span className="text-gray-500 cursor-help">Routes:</span></Tooltip> <span className="font-medium">{formula.routes?.replace(/[{}"]/g, '') || '—'}</span></div>
        <div><Tooltip text="Where this formula originated from"><span className="text-gray-500 cursor-help">Source:</span></Tooltip> <span className="font-medium">{formula.formulaSourceName || '—'}</span></div>
        <div><Tooltip text="Whether this formula requires sterile compounding"><span className="text-gray-500 cursor-help">Sterile:</span></Tooltip> <span className="font-medium">{formula.sterile ? 'Yes' : 'No'}</span></div>
      </div>

      {/* Ingredients */}
      {formula.ingredients && formula.ingredients.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Ingredients</h4>
          <div className="flex flex-wrap gap-2">
            {formula.ingredients.map((ing, idx) => (
              <span
                key={`${ing.setId}-${idx}`}
                className="inline-block px-2.5 py-1 rounded bg-purple-50 text-purple-700 text-sm font-mono"
              >
                {ing.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Alerts */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 mb-4 text-sm">
          {successMessage}
        </div>
      )}
      {saveError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">
          {saveError}
        </div>
      )}

      {/* Current assignments */}
      <div className="mb-6">
        <CurrentAssignments
          assignments={formula.activeAssignments ?? []}
          onReassign={(xlinkSetId) => setReassignTarget(xlinkSetId)}
          onRemove={handleRemove}
        />
      </div>

      {/* Taxonomy search */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">
          {reassignTarget ? 'Search for replacement taxonomy code' : 'Assign Taxonomy Code'}
        </h4>
        {reassignTarget && (
          <button
            onClick={() => setReassignTarget(null)}
            className="text-xs text-gray-500 hover:text-gray-700 mb-2"
          >
            Cancel reassignment
          </button>
        )}
        <TaxonomySearch onSelect={handleSelect} />
        {isSaving && <p className="text-xs text-gray-400 mt-2">Saving...</p>}
      </div>

      {/* Taxonomy browser with smart suggestions */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mt-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Browse Taxonomy Codes</h4>
        <TaxonomyBrowser
          onSelect={handleSelect}
          onBatchSelect={reassignTarget ? undefined : handleBatchSelect}
          formulaSetId={formula.setId}
          isSaving={isSaving}
          excludeSetIds={new Set((formula.activeAssignments ?? []).map((a) => a.taxonomyCode.setId))}
        />
      </div>
    </div>
  );
}
